/**
 * Modal para crear/editar insumos nutricionales
 * Formulario completo con todos los campos nutricionales MINSAL
 */
import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Button,
  Space,
  message,
  Divider,
  Typography,
  Row,
  Col,
  Select,
  Alert,
  Tooltip,
} from "antd";
import {
  ExperimentOutlined,
  QuestionCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useBranch } from "../../context/BranchContext";
import api from "../../api/endpoints";

const { Text, Paragraph } = Typography;

const NutritionalIngredientModal = ({
  visible,
  ingredient,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { branches, activeBranch } = useBranch();

  useEffect(() => {
    if (visible) {
      if (ingredient) {
        form.setFieldsValue({
          ...ingredient,
          branch: ingredient.branch?.id || ingredient.branch,
        });
      } else {
        form.resetFields();
        // Establecer sucursal activa por defecto
        form.setFieldsValue({
          measurement_unit: "g",
          branch: activeBranch,
        });
      }
    }
  }, [visible, ingredient, form, activeBranch]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Validar formulario antes de continuar
      try {
        await form.validateFields();
      } catch (error) {
        message.error("Por favor completa todos los campos requeridos");
        setLoading(false);
        return;
      }

      // Asegurar que branch tenga un valor válido
      const branchValue = values.branch || activeBranch;

      // Preparar datos del producto con información nutricional
      const productData = {
        name: values.name,
        supplier_company: values.supplier_company || null,
        measurement_unit: values.measurement_unit || "g",
        branch: branchValue,
        // Marcar como ingrediente nutricional
        is_nutritional_ingredient: true,
        // Campos nutricionales (por 100g según MINSAL)
        energy_kcal: values.energy_kcal || null,
        proteins_g: values.proteins_g || null,
        total_fats_g: values.total_fats_g || null,
        saturated_fats_g: values.saturated_fats_g || null,
        monounsaturated_fats_g: values.monounsaturated_fats_g || null,
        polyunsaturated_fats_g: values.polyunsaturated_fats_g || null,
        cholesterol_mg: values.cholesterol_mg || null,
        carbohydrates_g: values.carbohydrates_g || null,
        total_sugars_g: values.total_sugars_g || null,
        sodium_mg: values.sodium_mg || null,
        dietary_fiber_g: values.dietary_fiber_g || null,
        // Configuración básica del producto
        is_for_sale: false,
        tracks_inventory: true,
      };

      console.log(`${ingredient ? "Actualizando" : "Creando"} ingrediente con datos:`, productData);

      if (ingredient) {
        // Actualizar producto existente
        const updated = await api.products.update(ingredient.id, productData);
        console.log("Ingrediente actualizado exitosamente:", updated);
        message.success("Insumo nutricional actualizado exitosamente");
      } else {
        // Crear nuevo producto
        const created = await api.products.create(productData);
        console.log("Ingrediente creado exitosamente:", created);
        message.success("Insumo nutricional creado exitosamente");
      }

      form.resetFields();
      
      // Pequeño delay para asegurar que localStorage se actualice
      setTimeout(() => {
        onSuccess();
      }, 100);
    } catch (error) {
      console.error("Error guardando insumo nutricional:", error);
      message.error(
        `Error al ${ingredient ? "actualizar" : "crear"} insumo: ${
          error.message || "Error desconocido"
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={
        ingredient ? "Editar Insumo Nutricional" : "Nuevo Insumo Nutricional"
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width="95%"
      style={{ maxWidth: 900 }}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          measurement_unit: "g",
        }}
      >
        {!ingredient && (
          <Alert
            message="💡 ¿Qué información necesito?"
            description={
              <div>
                <Paragraph style={{ marginBottom: 8 }}>
                  Aquí vas a registrar un ingrediente que usas para hacer tus helados (por ejemplo, leche, azúcar, crema). 
                  Necesitas copiar los números que vienen en la etiqueta del producto que compras.
                </Paragraph>
                <Paragraph style={{ marginBottom: 0 }}>
                  <strong>¿Dónde encuentro estos números?</strong> Toma el envase del producto que compras (por ejemplo, 
                  el envase de leche) y busca la tabla nutricional. Copia los números que dicen "por 100g" o "por 100ml" 
                  exactamente como aparecen.
                </Paragraph>
              </div>
            }
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            style={{ marginBottom: 24 }}
          />
        )}

        <Divider>
          <Space>
            <InfoCircleOutlined />
            <span>Información General</span>
          </Space>
        </Divider>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="name"
              label={
                <Space>
                  <span>¿Cómo se llama este ingrediente?</span>
                  <Tooltip title="Escribe el nombre del ingrediente que usas. Por ejemplo: 'Leche Entera', 'Azúcar', 'Crema de Leche'">
                    <QuestionCircleOutlined style={{ color: "#1890ff" }} />
                  </Tooltip>
                </Space>
              }
              rules={[{ required: true, message: "Necesitas poner un nombre para el ingrediente" }]}
              extra={<Text type="secondary" style={{ fontSize: 12 }}>Por ejemplo: Leche Entera, Azúcar Refinada, Crema de Leche</Text>}
            >
              <Input placeholder="Ej: Leche Entera" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="supplier_company"
              label={
                <Space>
                  <span>¿De qué marca o proveedor lo compras? (opcional)</span>
                  <Tooltip title="Si quieres, puedes escribir de dónde compras este ingrediente. Por ejemplo: 'Soprole', 'Colun'. Esto es opcional.">
                    <QuestionCircleOutlined style={{ color: "#1890ff" }} />
                  </Tooltip>
                </Space>
              }
              extra={<Text type="secondary" style={{ fontSize: 12 }}>Opcional. Por ejemplo: Soprole, Colun, Nestlé</Text>}
            >
              <Input placeholder="Ej: Soprole" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="measurement_unit"
              label={
                <Space>
                  <span>Unidad de Medida</span>
                  <Tooltip title="Unidad en que compras o almacenas este ingrediente. El sistema convertirá automáticamente a gramos para los cálculos.">
                    <QuestionCircleOutlined style={{ color: "#1890ff" }} />
                  </Tooltip>
                </Space>
              }
              rules={[{ required: true }]}
              extra={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Selecciona la unidad según cómo compras el ingrediente
                </Text>
              }
            >
              <Select placeholder="Seleccionar unidad">
                <Select.Option value="g">g (gramos)</Select.Option>
                <Select.Option value="kg">kg (kilogramos)</Select.Option>
                <Select.Option value="ml">ml (mililitros)</Select.Option>
                <Select.Option value="l">l (litros)</Select.Option>
                <Select.Option value="unidad">unidad</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="branch"
              label="Sucursal"
              rules={[{ required: true, message: "La sucursal es requerida" }]}
            >
              <Select placeholder="Seleccionar sucursal" showSearch>
                {branches.map((branch) => (
                  <Select.Option key={branch.value} value={branch.value}>
                    {branch.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider>
          <Space>
            <ExperimentOutlined />
            <span>Información Nutricional (por 100g o 100ml)</span>
          </Space>
        </Divider>

        <Alert
          message="📋 Importante: Los números deben ser 'por 100g' o 'por 100ml'"
          description={
            <div>
              <Paragraph style={{ marginBottom: 8 }}>
                Cuando copies los números de la etiqueta del producto, asegúrate de copiar los que dicen{" "}
                <strong>"por 100g"</strong> (si compras en gramos o kilos) o <strong>"por 100ml"</strong> (si compras en mililitros o litros).
              </Paragraph>
              <Paragraph style={{ marginBottom: 0 }}>
                <strong>Ejemplo práctico:</strong> Si compras leche en litros, busca en el envase la tabla que dice "por 100ml" 
                y copia esos números. Si compras azúcar en kilos, busca la tabla que dice "por 100g" y copia esos números.
              </Paragraph>
            </div>
          }
          type="warning"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 16 }}
        />

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="energy_kcal"
              label={
                <Space>
                  <span>¿Cuántas calorías tiene? (kcal)</span>
                  <Tooltip title="Busca en la etiqueta del producto el número que dice 'Valor energético' o 'Calorías'. Copia ese número.">
                    <QuestionCircleOutlined style={{ color: "#1890ff" }} />
                  </Tooltip>
                </Space>
              }
              extra={<Text type="secondary" style={{ fontSize: 12 }}>Ejemplo: La leche tiene aproximadamente 61 calorías por cada 100ml</Text>}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Ej: 61"
                min={0}
                step={0.1}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="proteins_g"
              label={
                <Space>
                  <span>¿Cuántas proteínas tiene? (g)</span>
                  <Tooltip title="Busca en la tabla nutricional del envase el número que dice 'Proteínas'. Copia ese número.">
                    <QuestionCircleOutlined style={{ color: "#1890ff" }} />
                  </Tooltip>
                </Space>
              }
              extra={<Text type="secondary" style={{ fontSize: 12 }}>Ejemplo: La leche tiene aproximadamente 3.2 gramos de proteínas por cada 100ml</Text>}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Ej: 3.2"
                min={0}
                step={0.1}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="total_fats_g"
              label={
                <Space>
                  <span>Grasas Totales (g)</span>
                  <Tooltip title="Total de grasas por 100g o 100ml. Busca 'Grasas totales' en la tabla nutricional.">
                    <QuestionCircleOutlined style={{ color: "#1890ff" }} />
                  </Tooltip>
                </Space>
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Ej: 3.2"
                min={0}
                step={0.1}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="saturated_fats_g"
              label={
                <Space>
                  <span>Grasas Saturadas (g)</span>
                  <Tooltip title="Grasas saturadas por 100g o 100ml. Busca 'Grasas saturadas' en la tabla nutricional.">
                    <QuestionCircleOutlined style={{ color: "#1890ff" }} />
                  </Tooltip>
                </Space>
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Ej: 2.0"
                min={0}
                step={0.1}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="monounsaturated_fats_g"
              label={
                <Space>
                  <span>Grasas Monoinsaturadas (g)</span>
                  <Tooltip title="Grasas monoinsaturadas por 100g o 100ml. Busca 'Grasas monoinsaturadas' en la tabla nutricional.">
                    <QuestionCircleOutlined style={{ color: "#1890ff" }} />
                  </Tooltip>
                </Space>
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Ej: 0.9"
                min={0}
                step={0.1}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="polyunsaturated_fats_g"
              label={
                <Space>
                  <span>Grasas Poliinsaturadas (g)</span>
                  <Tooltip title="Grasas poliinsaturadas por 100g o 100ml. Busca 'Grasas poliinsaturadas' en la tabla nutricional.">
                    <QuestionCircleOutlined style={{ color: "#1890ff" }} />
                  </Tooltip>
                </Space>
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Ej: 0.2"
                min={0}
                step={0.1}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="cholesterol_mg"
              label={
                <Space>
                  <span>Colesterol (mg)</span>
                  <Tooltip title="Miligramos de colesterol por 100g o 100ml. Busca 'Colesterol' en la tabla nutricional.">
                    <QuestionCircleOutlined style={{ color: "#1890ff" }} />
                  </Tooltip>
                </Space>
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Ej: 10"
                min={0}
                step={0.1}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="carbohydrates_g"
              label={
                <Space>
                  <span>Hidratos de Carbono (g)</span>
                  <Tooltip title="Carbohidratos totales por 100g o 100ml. Busca 'Hidratos de carbono' en la tabla nutricional.">
                    <QuestionCircleOutlined style={{ color: "#1890ff" }} />
                  </Tooltip>
                </Space>
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Ej: 4.7"
                min={0}
                step={0.1}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="total_sugars_g"
              label={
                <Space>
                  <span>Azúcares Totales (g)</span>
                  <Tooltip title="Azúcares totales por 100g o 100ml. Busca 'Azúcares' o 'Azúcares totales' en la tabla nutricional.">
                    <QuestionCircleOutlined style={{ color: "#1890ff" }} />
                  </Tooltip>
                </Space>
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Ej: 4.7"
                min={0}
                step={0.1}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="sodium_mg"
              label={
                <Space>
                  <span>Sodio (mg)</span>
                  <Tooltip title="Miligramos de sodio por 100g o 100ml. Busca 'Sodio' en la tabla nutricional.">
                    <QuestionCircleOutlined style={{ color: "#1890ff" }} />
                  </Tooltip>
                </Space>
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Ej: 50"
                min={0}
                step={0.1}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="dietary_fiber_g"
              label={
                <Space>
                  <span>Fibra Dietética (g)</span>
                  <Tooltip title="Gramos de fibra dietética por 100g o 100ml. Busca 'Fibra dietética' en la tabla nutricional.">
                    <QuestionCircleOutlined style={{ color: "#1890ff" }} />
                  </Tooltip>
                </Space>
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Ej: 0"
                min={0}
                step={0.1}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item>
          <Space>
            <Button onClick={handleCancel}>Cancelar</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {ingredient ? "Actualizar" : "Crear Insumo"}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default NutritionalIngredientModal;

